import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';

const { FiShield, FiLock, FiEye, FiServer, FiMail, FiMapPin, FiPhone } = FiIcons;

function PrivacyPolicy() {
  return (
    <>
      <SEOHead
        title="Privacy Policy - ResizeImageFast Data Protection & Security"
        description="Read our comprehensive privacy policy to understand how ResizeImageFast protects your data. We don't store images, collect minimal data, and prioritize your privacy with local image processing."
        keywords="privacy policy, data protection, image privacy, GDPR compliance, data security, privacy rights, image processing privacy, ResizeImageFast privacy"
        url="/privacy"
        image="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&h=630&fit=crop"
        type="article"
        article={{
          publishedTime: "2024-12-01T00:00:00Z",
          modifiedTime: new Date().toISOString(),
          author: "Andrea Bertolotti",
          section: "Legal",
          tags: ["Privacy", "Data Protection", "GDPR", "Security"]
        }}
      />
      
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiShield} className="text-blue-600 text-2xl" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
              <p className="text-gray-600">
                Last updated: December 2024
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                  <SafeIcon icon={FiLock} className="mr-2" />
                  Your Privacy Matters
                </h2>
                <p className="text-gray-700 mb-4">
                  At ResizeImageFast.com, operated by BertoProduction, we are committed to protecting your privacy. 
                  This Privacy Policy explains how our Free Image Resizer tool handles your data and what measures 
                  we take to ensure your information remains secure.
                </p>
                <p className="text-gray-700">
                  Our image resizer operates entirely in your browser, meaning your images never leave your 
                  device during the resizing process.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                  <SafeIcon icon={FiServer} className="mr-2" />
                  Data We Don't Collect
                </h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <span className="text-green-600 mr-2">✓</span>
                      We do not store your uploaded images on our servers
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-600 mr-2">✓</span>
                      We do not collect personal information like names or email addresses
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-600 mr-2">✓</span>
                      We do not track your browsing behavior across other websites
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-600 mr-2">✓</span>
                      We do not sell or share your data with third parties
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                  <SafeIcon icon={FiEye} className="mr-2" />
                  Information We May Collect
                </h2>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Analytics Data</h3>
                    <p>
                      We use Google Analytics to understand how visitors use our website. This includes:
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Page views and session duration</li>
                      <li>Browser type and operating system</li>
                      <li>Country and city (but not your exact location)</li>
                      <li>How you found our website</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Cookies</h3>
                    <p>
                      We use minimal cookies to:
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Remember your cookie preferences</li>
                      <li>Analyze website traffic (via Google Analytics)</li>
                      <li>Display relevant advertisements (via Google AdSense)</li>
                      <li>Improve your user experience</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">How Our Image Resizer Works</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <p className="text-gray-700 mb-4">
                    <strong>Complete Privacy:</strong> Our image resizer uses advanced HTML5 Canvas technology 
                    to process your images entirely within your web browser. This means:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">•</span>
                      Your images never leave your device
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">•</span>
                      No uploads to our servers are required
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">•</span>
                      Processing happens locally on your computer
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">•</span>
                      You maintain complete control over your images
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Google Analytics</h3>
                    <p>
                      We use Google Analytics (ID: G-8J3NTMVG8P) to understand website usage. Google Analytics 
                      may collect information about your visit, but this data is aggregated and anonymous. 
                      You can opt out of Google Analytics by installing the 
                      <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                        Google Analytics Opt-out Browser Add-on
                      </a>.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Google AdSense</h3>
                    <p>
                      We display advertisements through Google AdSense (Publisher ID: ca-pub-2861454200023917) 
                      to support our free service. These ads may use cookies to show you relevant advertisements 
                      based on your interests. You can control ad personalization in your 
                      <a href="https://adssettings.google.com" className="text-blue-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                        Google Ads Settings
                      </a>.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights and Choices</h2>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>Cookie Control:</strong> You can control cookies through your browser settings. 
                    Most browsers allow you to refuse cookies or alert you when cookies are being sent.
                  </p>
                  <p>
                    <strong>Analytics Opt-out:</strong> You can opt out of Google Analytics tracking using 
                    their official browser add-on or by enabling "Do Not Track" in your browser.
                  </p>
                  <p>
                    <strong>Ad Personalization:</strong> You can disable personalized ads through Google's 
                    Ad Settings or use an ad blocker if you prefer not to see advertisements.
                  </p>
                  <p>
                    <strong>Data Deletion:</strong> Since we don't store your images or personal data, 
                    there's nothing for us to delete. Your images remain on your device only.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Security</h2>
                <p className="text-gray-700 mb-4">
                  We implement appropriate security measures to protect against unauthorized access, 
                  alteration, disclosure, or destruction of information. Since image processing happens 
                  in your browser, your images benefit from the same security measures your browser 
                  provides.
                </p>
                <p className="text-gray-700">
                  We recommend using the latest version of your web browser and keeping your device's 
                  security software up to date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Controller Information</h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <p className="text-gray-700 mb-4">
                    The data controller for this website is:
                  </p>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-start">
                      <SafeIcon icon={FiMail} className="mr-3 mt-1 text-blue-600" />
                      <div>
                        <p><strong>Name:</strong> Andrea Bertolotti (BertoProduction)</p>
                        <p><strong>Email:</strong> 
                          <a href="mailto:info@bertoproduction.com" className="text-blue-600 hover:underline ml-1">
                            info@bertoproduction.com
                          </a>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <SafeIcon icon={FiMapPin} className="mr-3 mt-1 text-blue-600" />
                      <div>
                        <p><strong>Address:</strong></p>
                        <p>Arona, Canary Islands</p>
                        <p>S.C. de Tenerife, Spain, 38626</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <SafeIcon icon={FiPhone} className="mr-3 mt-1 text-blue-600" />
                      <div>
                        <p><strong>Phone:</strong> 
                          <a href="tel:+34642643499" className="text-blue-600 hover:underline ml-1">
                            +34 642 643 499
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
                <p className="text-gray-700">
                  We may update this Privacy Policy from time to time. We will notify you of any 
                  changes by posting the new Privacy Policy on this page and updating the "Last updated" 
                  date at the top of this policy. Continued use of our service after any changes 
                  constitutes acceptance of the new Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <p className="text-gray-700 mb-4">
                    If you have any questions about this Privacy Policy, our data practices, or wish to 
                    exercise your privacy rights, please contact us:
                  </p>
                  <div className="space-y-2 text-gray-700">
                    <p>
                      <strong>Privacy Inquiries:</strong> 
                      <a href="mailto:info@bertoproduction.com" className="text-blue-600 hover:underline ml-1">
                        info@bertoproduction.com
                      </a>
                    </p>
                    <p>
                      <strong>Website:</strong> 
                      <a href="https://resizeimagefast.com" className="text-blue-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                        ResizeImageFast.com
                      </a>
                    </p>
                    <p>
                      <strong>Company:</strong> 
                      <a href="https://bertoproduction.com" className="text-blue-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                        BertoProduction.com
                      </a>
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="font-semibold text-green-800 mb-2">Privacy Summary</h3>
                  <p className="text-green-700">
                    Your privacy is our priority. We don't store your images, we don't collect personal 
                    information, and all image processing happens securely in your browser. We use minimal 
                    analytics and advertising services to support our free tool. You can use our service 
                    with confidence knowing your data stays with you.
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default PrivacyPolicy;